<template>
  <div id="app">
    <header class="site-header">
      <nav class="site-nav" aria-label="Primary navigation">
        <router-link class="brand" :to="{ name: 'home', query: $route.query }" aria-label="DBD Perk Roulette home">
          <span class="brand__mark"><i></i><i></i><i></i><i></i></span>
          <span><strong>DBD PERK ROULETTE</strong><small>LOADOUT + BUILD LAB</small></span>
        </router-link>
        <div class="site-nav__links">
          <router-link :to="{ name: 'roulette', query: $route.query }">{{ $t('forge.nav.roulette') }}</router-link>
          <router-link :to="{ name: 'buildLab', query: $route.query }">{{ $t('forge.nav.buildLab') }}</router-link>
          <router-link :to="{ name: 'intelligence', query: $route.query }">{{ $t('forge.nav.intelligence') }}</router-link>
          <router-link :to="{ name: 'home', hash: '#about', query: $route.query }">{{ $t('forge.nav.about') }}</router-link>
        </div>
        <label class="language-control">
          <span>{{ $t('forge.nav.language') }}</span>
          <select v-model="activeLocale" aria-label="Language" @change="changeLanguage">
            <option v-for="locale in locales" :key="locale.code" :value="locale.code">{{ locale.short }} · {{ locale.label }}</option>
          </select>
        </label>
      </nav>
    </header>

    <main :class="['site-container', { 'site-container--home': $route.name === 'home' }]">
      <router-view />
    </main>

    <footer class="site-footer" id="about">
      <div class="site-footer__brand"><strong>DBD PERK ROULETTE</strong><span>{{ $t('forge.footer.tagline') }}</span></div>
      <p>{{ $t('forge.footer.disclaimer') }}</p>
      <div class="site-footer__meta"><span>© {{ currentYear }} Fan project</span><span>{{ $t('forge.footer.privacy') }}</span></div>
    </footer>
  </div>
</template>

<script>
const AVAILABLE_LOCALES = ['en', 'fr', 'de', 'es', 'ja']

export default {
  name: 'App',
  data () {
    return {
      activeLocale: 'en',
      locales: [
        { code: 'en', short: 'EN', label: 'English' },
        { code: 'fr', short: 'FR', label: 'Français' },
        { code: 'de', short: 'DE', label: 'Deutsch' },
        { code: 'es', short: 'ES', label: 'Español' },
        { code: 'ja', short: 'JA', label: '日本語' }
      ],
      currentYear: new Date().getFullYear()
    }
  },
  watch: {
    '$route.name': function () {
      this.updateMetadata()
    },
    '$route.query.lang': function (locale) {
      if (AVAILABLE_LOCALES.indexOf(locale) !== -1 && locale !== this.activeLocale) {
        this.activeLocale = locale
        this.$i18n.locale = locale
        this.updateMetadata()
      }
    }
  },
  mounted () {
    let locale = String(this.$route.query.lang || '').toLowerCase()
    if (AVAILABLE_LOCALES.indexOf(locale) === -1) {
      let storedLocale = ''
      try { storedLocale = window.localStorage.getItem('dbd-perk-roulette-language') || '' } catch (error) {}
      const browserLocale = String(navigator.language || 'en').slice(0, 2).toLowerCase()
      locale = AVAILABLE_LOCALES.indexOf(storedLocale) !== -1
        ? storedLocale
        : AVAILABLE_LOCALES.indexOf(browserLocale) !== -1 ? browserLocale : 'en'
    }
    this.activeLocale = locale
    this.$i18n.locale = locale
    document.documentElement.lang = locale
    this.$nextTick(() => this.updateMetadata())
  },
  methods: {
    changeLanguage () {
      const query = Object.assign({}, this.$route.query, { lang: this.activeLocale })
      this.$i18n.locale = this.activeLocale
      document.documentElement.lang = this.activeLocale
      try { window.localStorage.setItem('dbd-perk-roulette-language', this.activeLocale) } catch (error) {}
      this.$router.replace({ path: this.$route.path, query, hash: this.$route.hash }).catch(() => {})
      this.updateMetadata()
    },
    updateMetadata () {
      const routeKey = this.$route.name === 'roulette'
        ? 'roulette'
        : this.$route.name === 'buildLab' ? 'build' : this.$route.name === 'intelligence' ? 'intelligence' : 'home'
      const isIntelligence = routeKey === 'intelligence'
      const title = this.$t(isIntelligence ? 'forge.intelligence.metaTitle' : `forge.meta.${routeKey}Title`)
      const description = this.$t(isIntelligence ? 'forge.intelligence.metaDescription' : `forge.meta.${routeKey}Description`)
      document.title = title
      const meta = document.querySelector('meta[name="description"]')
      if (meta) meta.setAttribute('content', description)
      const ogTitle = document.querySelector('meta[property="og:title"]')
      const ogDescription = document.querySelector('meta[property="og:description"]')
      if (ogTitle) ogTitle.setAttribute('content', title)
      if (ogDescription) ogDescription.setAttribute('content', description)
    }
  }
}
</script>

<style lang="scss">
@import "../public/sprites/surv-css.css";
@import "../public/sprites/kill-css.css";

:root {
  color-scheme: dark;
  --ink: #080b0d;
  --panel: #10161a;
  --paper: #f4f3ed;
  --muted: rgba(244, 243, 237, .54);
  --cyan: #68dcff;
  --coral: #ff7f6d;
  --mint: #82e8ba;
}

* { box-sizing: border-box; margin: 0; padding: 0; }
html { min-width: 320px; scroll-behavior: smooth; }
body { min-width: 320px; min-height: 100vh; background: radial-gradient(circle at 12% 8%, rgba(104,220,255,.055), transparent 28%), radial-gradient(circle at 88% 4%, rgba(255,127,109,.045), transparent 24%), #070a0c; color: var(--paper); font-family: Inter, "Segoe UI Variable", "Segoe UI", Arial, sans-serif; line-height: 1.4; }
button, input, select { font: inherit; }
button, a { -webkit-tap-highlight-color: transparent; }
button:focus-visible, a:focus-visible, select:focus-visible, input:focus-visible { outline: 2px solid var(--cyan); outline-offset: 3px; }
::selection { background: var(--cyan); color: #071014; }
#app { min-height: 100vh; }
.site-header { height: 78px; border-bottom: 1px solid rgba(255,255,255,.07); background: rgba(7,10,12,.78); backdrop-filter: blur(22px); position: sticky; top: 0; z-index: 100; }
.site-nav { width: min(1480px, calc(100% - 32px)); height: 100%; margin: 0 auto; display: flex; align-items: center; gap: 34px; }
.brand { color: var(--paper); display: flex; align-items: center; gap: 11px; text-decoration: none; }
.brand__mark { width: 29px; height: 35px; display: flex; align-items: flex-end; gap: 2px; transform: skew(-7deg); }
.brand__mark i { width: 5px; height: 26px; border-radius: 1px; background: var(--paper); display: block; }
.brand__mark i:nth-child(2) { height: 32px; background: var(--cyan); } .brand__mark i:nth-child(3) { height: 22px; } .brand__mark i:nth-child(4) { height: 29px; background: var(--coral); }
.brand strong { display: block; font-size: 14px; font-weight: 950; letter-spacing: -.02em; }
.brand small { color: rgba(255,255,255,.31); display: block; font-size: 7px; letter-spacing: .19em; }
.site-nav__links { margin: 0 auto; display: flex; align-items: center; gap: 4px; }
.site-nav__links a { min-height: 38px; padding: 0 14px; border-radius: 10px; color: rgba(255,255,255,.44); display: flex; align-items: center; font-size: 9px; font-weight: 900; letter-spacing: .11em; text-decoration: none; text-transform: uppercase; transition: color .18s ease, background .18s ease; }
.site-nav__links a:hover, .site-nav__links a.router-link-active { background: rgba(255,255,255,.055); color: #fff; }
.language-control { display: flex; align-items: center; gap: 9px; }
.language-control > span { color: rgba(255,255,255,.29); font-size: 8px; letter-spacing: .11em; text-transform: uppercase; }
.language-control select { min-height: 36px; max-width: 140px; padding: 0 28px 0 10px; border: 1px solid rgba(255,255,255,.1); border-radius: 9px; background: #0d1215; color: rgba(255,255,255,.72); cursor: pointer; font-size: 9px; font-weight: 800; }
.site-container { width: min(1480px, calc(100% - 32px)); min-height: calc(100vh - 78px); margin: 0 auto; padding: 24px 0 70px; }
.site-footer { width: min(1480px, calc(100% - 32px)); margin: 0 auto; padding: 28px 0 34px; border-top: 1px solid rgba(255,255,255,.07); color: rgba(255,255,255,.3); display: grid; grid-template-columns: .6fr 1.6fr .8fr; align-items: center; gap: 28px; font-size: 9px; line-height: 1.6; }
.site-footer__brand strong { color: rgba(255,255,255,.68); display: block; font-size: 12px; }
.site-footer__brand span { font-size: 8px; letter-spacing: .08em; }
.site-footer__meta { text-align: right; }
.site-footer__meta span { display: block; }
@media (max-width: 760px) { .site-header { height: auto; min-height: 70px; } .site-nav { width: calc(100% - 20px); padding: 9px 0; gap: 10px; flex-wrap: wrap; } .brand { margin-right: auto; } .language-control > span { display: none; } .language-control select { max-width: 80px; } .site-nav__links { width: 100%; order: 3; } .site-nav__links a { flex: 1; justify-content: center; } .site-container { width: calc(100% - 20px); padding-top: 14px; } .site-footer { grid-template-columns: 1fr; text-align: left; } .site-footer__meta { text-align: left; } }
@media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto; } *, *::before, *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; } }
</style>
